import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Progress,
  Spin,
  message,
  Table,
  Tag,
  Space,
  DatePicker,
  Button,
  Tabs,
  Avatar,
  Typography,
  Select,
} from 'antd';
import {
  BarChartOutlined,
  DollarOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  WalletOutlined,
  HistoryOutlined,
  SendOutlined,
  PieChartOutlined,
  LineChartOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import llmService from '../../services/llmService';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// 配额转换为美元 (500000 单位 = $1，即 1 单位 = $0.000002)
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(2)}K`;
  }
  if (dollars >= 0.01) {
    return `$${dollars.toFixed(2)}`;
  }
  return `$${dollars.toFixed(4)}`;
};

// 格式化数字
const formatNumber = (num) => {
  if (num === undefined || num === null || isNaN(num)) return '0';
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(2)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(2)}K`;
  }
  return num.toLocaleString();
};

const Stats = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(7, 'day'),
    dayjs(),
  ]);
  const [chartType, setChartType] = useState('quota'); // quota, count, tokens

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      // 构建日期参数
      const params = {};
      if (dateRange && dateRange.length === 2) {
        params.start_timestamp = Math.floor(dateRange[0].startOf('day').valueOf() / 1000);
        params.end_timestamp = Math.floor(dateRange[1].endOf('day').valueOf() / 1000);
      }

      const response = await llmService.getStats(params);
      if (response.success) {
        // 处理返回数据，将 created_at 转换为 day
        const processedData = { ...response.data };
        if (processedData.quota_dates) {
          processedData.quota_dates = processedData.quota_dates.map(item => ({
            ...item,
            // created_at 是秒级时间戳，转换为日期字符串
            day: item.created_at ? dayjs(item.created_at * 1000).format('YYYY-MM-DD') : item.day || 'Unknown',
            // token_used 可能是总 token，拆分为 prompt 和 completion
            prompt_tokens: item.prompt_tokens || item.token_used || 0,
            completion_tokens: item.completion_tokens || 0,
          }));
        }
        setStats(processedData);
      } else {
        message.error(response.error || t('Failed to retrieve statistics'));
      }
    } catch (error) {
      message.error(t('Failed to retrieve statistics'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
  };

  // 每日趋势图
  const dailyTrendOption = useMemo(() => {
    const quotaDates = stats?.quota_dates || [];
    if (quotaDates.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    // 按日期聚合数据
    const dateMap = {};
    quotaDates.forEach(item => {
      const day = item.day || 'Unknown';
      if (!dateMap[day]) {
        dateMap[day] = { quota: 0, count: 0, tokens: 0 };
      }
      dateMap[day].quota += item.quota || 0;
      dateMap[day].count += item.count || 0;
      dateMap[day].tokens += item.token_used || item.prompt_tokens || 0;
    });

    const dates = Object.keys(dateMap).sort();
    const quotaData = dates.map(d => dateMap[d].quota);
    const countData = dates.map(d => dateMap[d].count);
    const tokenData = dates.map(d => dateMap[d].tokens);

    const yAxisConfig = chartType === 'quota' ? {
      type: 'value',
      axisLabel: { formatter: v => quotaToDollar(v) }
    } : { type: 'value' };

    const seriesData = chartType === 'quota' ? quotaData :
                       chartType === 'count' ? countData : tokenData;
    const seriesName = chartType === 'quota' ? t('Quota consumed') :
                       chartType === 'count' ? t('Request count') : t('Token usage');

    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const value = params[0].value;
          const formatted = chartType === 'quota' ? quotaToDollar(value) : formatNumber(value);
          return `${params[0].name}<br/>${seriesName}: ${formatted}`;
        }
      },
      xAxis: { type: 'category', data: dates },
      yAxis: yAxisConfig,
      series: [{
        name: seriesName,
        data: seriesData,
        type: 'line',
        smooth: true,
        areaStyle: { opacity: 0.3 },
        lineStyle: { color: chartType === 'quota' ? '#1890ff' : chartType === 'count' ? '#52c41a' : '#faad14' },
        itemStyle: { color: chartType === 'quota' ? '#1890ff' : chartType === 'count' ? '#52c41a' : '#faad14' },
      }],
      grid: { left: '10%', right: '5%', bottom: '10%', top: '10%' },
    };
  }, [stats, chartType, t]);

  // 模型消耗分布饼图
  const modelPieOption = useMemo(() => {
    const quotaDates = stats?.quota_dates || [];
    if (quotaDates.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    const modelQuota = {};
    quotaDates.forEach(item => {
      const model = item.model_name || 'Unknown';
      modelQuota[model] = (modelQuota[model] || 0) + (item.quota || 0);
    });

    const pieData = Object.entries(modelQuota)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params) => `${params.name}: ${quotaToDollar(params.value)} (${params.percent}%)`
      },
      legend: { orient: 'vertical', right: 10, top: 'center', type: 'scroll' },
      series: [{
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['40%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
        label: { show: false },
        emphasis: { label: { show: true, fontSize: 14, fontWeight: 'bold' } },
        data: pieData,
      }],
    };
  }, [stats, t]);

  // 模型请求次数排行
  const modelBarOption = useMemo(() => {
    const quotaDates = stats?.quota_dates || [];
    if (quotaDates.length === 0) {
      return { title: { text: t('No data yet'), left: 'center', top: 'center' } };
    }

    const modelCount = {};
    quotaDates.forEach(item => {
      const model = item.model_name || 'Unknown';
      modelCount[model] = (modelCount[model] || 0) + (item.count || 0);
    });

    const sortedData = Object.entries(modelCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96', '#fa8c16', '#a0d911', '#2f54eb'];

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'value' },
      yAxis: {
        type: 'category',
        data: sortedData.map(d => d.name).reverse(),
        axisLabel: { width: 100, overflow: 'truncate' }
      },
      series: [{
        type: 'bar',
        data: sortedData.map(d => d.value).reverse(),
        itemStyle: {
          color: (params) => colors[params.dataIndex % colors.length],
          borderRadius: [0, 4, 4, 0],
        },
      }],
      grid: { left: '30%', right: '5%', bottom: '10%', top: '10%' },
    };
  }, [stats, t]);

  // 统计数据计算
  const statsComputed = useMemo(() => {
    const quotaDates = stats?.quota_dates || [];
    const totalQuota = quotaDates.reduce((sum, item) => sum + (item.quota || 0), 0);
    const totalCount = quotaDates.reduce((sum, item) => sum + (item.count || 0), 0);
    // token_used 是 API 返回的字段，prompt_tokens 是备选
    const totalTokens = quotaDates.reduce((sum, item) => sum + (item.token_used || item.prompt_tokens || 0), 0);
    const modelCount = new Set(quotaDates.map(item => item.model_name)).size;

    // 计算今日和本月请求数
    const today = dayjs().format('YYYY-MM-DD');
    const thisMonth = dayjs().format('YYYY-MM');

    const todayCount = quotaDates
      .filter(item => item.day === today)
      .reduce((sum, item) => sum + (item.count || 0), 0);

    const monthCount = quotaDates
      .filter(item => item.day && item.day.startsWith(thisMonth))
      .reduce((sum, item) => sum + (item.count || 0), 0);

    return { totalQuota, totalCount, totalTokens, modelCount, todayCount, monthCount };
  }, [stats]);

  if (loading && !stats) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const quotaInfo = stats?.quota || {};
  const logStats = stats?.log_stats || {};
  const quotaDates = stats?.quota_dates || [];

  const usagePercent = quotaInfo.usage_percent || 0;

  const quotaColumns = [
    {
      title: t('Date'),
      dataIndex: 'day',
      key: 'day',
      width: 120,
      sorter: (a, b) => (a.day || '').localeCompare(b.day || ''),
    },
    {
      title: t('Model'),
      dataIndex: 'model_name',
      key: 'model_name',
      render: (model) => <Tag color="blue">{model}</Tag>,
    },
    {
      title: t('Quota consumed'),
      dataIndex: 'quota',
      key: 'quota',
      align: 'right',
      sorter: (a, b) => (a.quota || 0) - (b.quota || 0),
      render: (quota) => quotaToDollar(quota),
    },
    {
      title: t('Request count'),
      dataIndex: 'count',
      key: 'count',
      align: 'right',
      sorter: (a, b) => (a.count || 0) - (b.count || 0),
      render: formatNumber,
    },
    {
      title: t('Token usage'),
      dataIndex: 'token_used',
      key: 'token_used',
      align: 'right',
      sorter: (a, b) => (a.token_used || 0) - (b.token_used || 0),
      render: (tokens, record) => formatNumber(tokens || record.prompt_tokens || 0),
    },
  ];

  return (
    <div>
      {/* 头部操作栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Space>
          <BarChartOutlined style={{ fontSize: 20 }} />
          <Text strong style={{ fontSize: 18 }}>{t('Usage statistics')}</Text>
        </Space>
        <Space>
          <RangePicker
            value={dateRange}
            onChange={(dates) => dates && setDateRange(dates)}
            allowClear={false}
            size="small"
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={loading}
          >
            {t('Refresh')}
          </Button>
        </Space>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, #e6f4ff 0%, #f0f5ff 100%)' }}>
            <Statistic
              title={t('Total quota')}
              value={quotaToDollar(quotaInfo.quota)}
              prefix={<Avatar size="small" style={{ backgroundColor: '#1890ff' }} icon={<WalletOutlined />} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, #fff7e6 0%, #fffbe6 100%)' }}>
            <Statistic
              title={t('Used')}
              value={quotaToDollar(quotaInfo.used_quota)}
              prefix={<Avatar size="small" style={{ backgroundColor: '#faad14' }} icon={<ThunderboltOutlined />} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, #f6ffed 0%, #fcffe6 100%)' }}>
            <Statistic
              title={t('Remaining quota')}
              value={quotaToDollar(quotaInfo.remaining)}
              valueStyle={{ color: quotaInfo.is_exceeded ? '#cf1322' : '#3f8600' }}
              prefix={<Avatar size="small" style={{ backgroundColor: quotaInfo.is_exceeded ? '#ff4d4f' : '#52c41a' }} icon={<DollarOutlined />} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12, background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)' }}>
            <Statistic
              title={t('Today\'s requests')}
              value={formatNumber(statsComputed.todayCount)}
              suffix={t('Requests')}
              prefix={<Avatar size="small" style={{ backgroundColor: '#722ed1' }} icon={<ClockCircleOutlined />} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 期间统计 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Spend in period')}
              value={quotaToDollar(statsComputed.totalQuota)}
              prefix={<RiseOutlined style={{ color: '#1890ff' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Requests in period')}
              value={formatNumber(statsComputed.totalCount)}
              suffix={t('Requests')}
              prefix={<SendOutlined style={{ color: '#52c41a' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Tokens in period')}
              value={formatNumber(statsComputed.totalTokens)}
              prefix={<BarChartOutlined style={{ color: '#faad14' }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small" style={{ borderRadius: 12 }}>
            <Statistic
              title={t('Models used')}
              value={statsComputed.modelCount}
              suffix={t('items')}
              prefix={<PieChartOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* 配额使用进度 */}
      <Card title={t('Quota usage')} style={{ marginBottom: 16, borderRadius: 12 }} size="small">
        <div style={{ marginBottom: 16 }}>
          <Progress
            percent={usagePercent}
            status={quotaInfo.is_exceeded ? 'exception' : 'active'}
            strokeColor={usagePercent > 80 ? '#ff4d4f' : usagePercent > 50 ? '#faad14' : '#52c41a'}
            format={(percent) => `${percent?.toFixed(1)}%`}
          />
        </div>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic title={t('Utilization')} value={`${usagePercent.toFixed(1)}%`} />
          </Col>
          <Col span={8}>
            <Statistic
              title={t('Status')}
              value={quotaInfo.is_exceeded ? t('Limit exceeded') : t('Normal')}
              valueStyle={{ color: quotaInfo.is_exceeded ? '#cf1322' : '#3f8600' }}
            />
          </Col>
          <Col span={8}>
            <Statistic title={t('Requests this month')} value={formatNumber(statsComputed.monthCount)} suffix={t('Requests')} />
          </Col>
        </Row>
      </Card>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <LineChartOutlined />
                <span>{t('Usage trends')}</span>
              </Space>
            }
            extra={
              <Select
                value={chartType}
                onChange={setChartType}
                size="small"
                style={{ width: 100 }}
                options={[
                  { value: 'quota', label: t('Quota') },
                  { value: 'count', label: t('Request') },
                  { value: 'tokens', label: 'Tokens' },
                ]}
              />
            }
            style={{ borderRadius: 12 }}
            size="small"
          >
            <ReactECharts option={dailyTrendOption} style={{ height: 250 }} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <PieChartOutlined />
                <span>{t('Spend by model')}</span>
              </Space>
            }
            style={{ borderRadius: 12 }}
            size="small"
          >
            <ReactECharts option={modelPieOption} style={{ height: 250 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <BarChartOutlined />
                <span>{t('Models ranked by requests')}</span>
              </Space>
            }
            style={{ borderRadius: 12 }}
            size="small"
          >
            <ReactECharts option={modelBarOption} style={{ height: 300 }} />
          </Card>
        </Col>
      </Row>

      {/* 明细表格 */}
      <Card
        title={
          <Space>
            <BarChartOutlined />
            <span>{t('Daily usage details')}</span>
          </Space>
        }
        style={{ borderRadius: 12 }}
        size="small"
      >
        <Table
          columns={quotaColumns}
          dataSource={quotaDates}
          rowKey={(record, index) => `${record.day}-${record.model_name}-${index}`}
          pagination={{ pageSize: 10, showSizeChanger: true, showQuickJumper: true }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default Stats;
